// eslint-disable-next-line no-redeclare
/* global process */
/* global ga */

import HelperUtils from '@haptik/helpers/build/esm/platformUtils';

// Constants
import {
  ActionTypes,
  AlertTypes,
  FEATURE_FLAG_DATA,
} from 'constants/MogamboConstants';

import { dispatch } from '../dispatcher';

// Store
import BotStore from '../store';

export function userHasPermission(required) {
  return BotStore.getPermissions().indexOf(required) > -1;
}

export function isNewPipeline(domain) {
  return domain.pipeline_version ? domain.pipeline_version > 1 : false;
}

export function createAlert(message, alertLength, messageType) {
  dispatch('CREATE_ALERT', {
    status: true,
    message,
    alertLength: alertLength || 3500,
    messageType,
  });
}

export function permissionErrorAlert(msg) {
  createAlert(
    `You do not have permissions to ${msg || 'do this'}`,
    undefined,
    AlertTypes.NEGATIVE
  );
}

export function destroyAlert() {
  dispatch('DESTROY_ALERT', {
    status: false,
  });
}

export function startLoading(message) {
  dispatch(ActionTypes.START_LOADING, {
    message,
  });
}

export function stopLoading(message) {
  dispatch(ActionTypes.STOP_LOADING, {
    message,
  });
}

export function sendGoogleAnalyticsEvents(
  eventCategory,
  eventAction,
  eventLabel,
  eventValue
) {
  if (process.env.NODE_ENV === 'production') {
    ga('send', {
      hitType: 'event',
      eventCategory,
      eventAction,
      eventLabel,
      eventValue,
    });
  }
}

export function setCorsEnableFlag(clientId) {
  const featureData = BotStore.getFeaturesData();
  const featureName = FEATURE_FLAG_DATA.FLAGS.ENABLE_CORS_HEADER_ORIGIN;

  const isFeatureEnabled = new HelperUtils.IsFeatureEnabled(
    featureData,
    featureName
  );
  const isEnabled = isFeatureEnabled.forPartner(clientId);
  BotStore.setCorsEnableFlag(isEnabled);
  BotStore.emitChange();
}

export function selfServeCheck(partner) {
  const featureData = BotStore.getFeaturesData();
  const featureName = 'enable_self_serve_features';

  const isFeatureEnabled = new HelperUtils.IsFeatureEnabled(
    featureData,
    featureName
  );

  const isEnabled = isFeatureEnabled.forPartner(partner);

  BotStore.setIsSelfServe(isEnabled);
}

export function setEnableWhatsappPayFlag(businessViaName) {
  const featureData = BotStore.getFeaturesData();
  const featureName = 'enable_whatsapp_pay';

  const isFeatureEnabled = new HelperUtils.IsFeatureEnabled(
    featureData,
    featureName
  );

  const isEnabled = isFeatureEnabled.forBusiness(businessViaName);

  BotStore.setWhatsappPay(isEnabled);
}

export function showHaptikBrandingOptionCheck(businessViaName) {
  const featureData = BotStore.getFeaturesData();
  const featureName = 'show_haptik_branding_option';

  const isFeatureEnabled = new HelperUtils.IsFeatureEnabled(
    featureData,
    featureName
  );

  return isFeatureEnabled.forBusiness(businessViaName);
}

export function entityV2EnabledCheck(domainTitle) {
  const featureData = BotStore.getFeaturesData();
  const featureName = 'enable_entity_v2';

  const isFeatureEnabled = new HelperUtils.IsFeatureEnabled(
    featureData,
    featureName
  );

  const isEnabled = isFeatureEnabled.forDomain(domainTitle);

  BotStore.setIsEntityV2Enabled(isEnabled);
}

// CAS Name
export function CASName() {
  return BotStore.getCAS();
}

/**
 * Debouncer
 * @param callback Function to be called
 * @param wait Execute the callback post this wait time
 *
 * Primarily used for live search in FE.
 * eg. Waits till user completed typing a keyword and will then initiate search
 * Waits till the time specified.
 */
export const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      // eslint-disable-next-line prefer-spread
      callback.apply(null, args);
    }, wait);
  };
};

// Universal Util to set feature flags
export function featureFlagCheck(
  featureName,
  checkFor,
  value,
  callback = () => null
) {
  const featureData = BotStore.getFeaturesData();
  let isEnabled = false;

  const isFeatureEnabled = new HelperUtils.IsFeatureEnabled(
    featureData,
    featureName
  );

  if (checkFor === FEATURE_FLAG_DATA.TYPES.DOMAIN) {
    isEnabled = isFeatureEnabled.forDomain(value);
  }

  if (checkFor === FEATURE_FLAG_DATA.TYPES.PARTNER) {
    isEnabled = isFeatureEnabled.forPartner(value);
  }

  if (checkFor === FEATURE_FLAG_DATA.TYPES.BUSINESS) {
    isEnabled = isFeatureEnabled.forBusiness(value);
  }

  BotStore.setFeatureFlag(featureName, isEnabled);
  BotStore.emitChange();

  if (callback) {
    callback(isEnabled);
  }
}

export const verifyEnableAissIntent = async (domainTitle) => {
  if (domainTitle) {
    await featureFlagCheck(
      FEATURE_FLAG_DATA.FLAGS.ENABLE_AISS_INTENT,
      FEATURE_FLAG_DATA.TYPES.DOMAIN,
      domainTitle
    );
  }
};

export const verifyEnableGenAiSalesAssistant = async (domainTitle) => {
  if (domainTitle) {
    await featureFlagCheck(
      FEATURE_FLAG_DATA.FLAGS.ENABLE_GEN_AI_SALES_ASSISTANT,
      FEATURE_FLAG_DATA.TYPES.DOMAIN,
      domainTitle
    );
  }
};

export const verifyDisableLanguageGuardrails = async (domainTitle) => {
  if (domainTitle) {
    await featureFlagCheck(
      FEATURE_FLAG_DATA.FLAGS.DISABLE_LANGUAGE_GUARDRAILS,
      FEATURE_FLAG_DATA.TYPES.DOMAIN,
      domainTitle
    );
  }
};

export const setRephraseResponsesFlag = async (domainTitle) => {
  if (domainTitle) {
    await featureFlagCheck(
      FEATURE_FLAG_DATA.FLAGS.ENABLE_REPHRASE_RESPONSES,
      FEATURE_FLAG_DATA.TYPES.DOMAIN,
      domainTitle
    );
  }
};

export const setFeedbackMessagesFlag = async (businessViaName) => {
  if (businessViaName) {
    await featureFlagCheck(
      FEATURE_FLAG_DATA.FLAGS.ENABLE_AGENT_FEEDBACK_MESSAGES,
      FEATURE_FLAG_DATA.TYPES.BUSINESS,
      businessViaName
    );
  }
};

export const verifyEnableContextualBotBreak = async (domainTitle) => {
  if (domainTitle) {
    await featureFlagCheck(
      FEATURE_FLAG_DATA.FLAGS.ENABLE_CONTEXTUAL_BOT_BREAK,
      FEATURE_FLAG_DATA.TYPES.DOMAIN,
      domainTitle
    );
  }
};
